import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MantaMethod, type MethodInitial } from '@/components/marketing/manta/v3/MantaMethod';
import { COURSE_HREF, DIAGNOSTIC_FACTS, MARKETING_CTAS } from '@/lib/marketingCtas';
import { hasAnyProgress, loadOpenProgress } from '@/lib/satOpenProgress';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

/**
 * The first screen of `/`: the copy, one filled primary, and the manta method stage
 * (specs/012-manta-character/DESIGN.md §11, replacing HERO-V2's boxed Course Window).
 *
 * The stage is open, unboxed type and line on the page background: no card, no chrome.
 * From lg it sits beside the copy (and at xl runs toward the viewport's right edge); below lg it
 * follows the copy and the CTA, so on a phone the primary is always above the animation.
 * Everything above the fold is static HTML at render: no network, no clock, the stage's
 * first frame is in the HTML and the character is placed in % before any script runs.
 * Hydration-only changes keep their boxes (CLS): the returning visitor's `Continue your
 * course →` swaps into the same fixed-width button, and the stage jumps to its finished
 * frame without changing size (its height comes from its viewBox).
 */
export function HomeHero() {
  const reduced = usePrefersReducedMotion();
  const [returning, setReturning] = useState(false);
  useEffect(() => {
    // Course progress only: a taster answer isn't a course (HERO-V2).
    setReturning(hasAnyProgress(loadOpenProgress()));
  }, []);
  const initial: MethodInitial = returning ? 'done' : reduced ? 'overview' : 'start';

  return (
    <section
      className="relative overflow-hidden lg:flex lg:min-h-[calc(100svh-3.5rem-6rem)] lg:items-center"
      data-testid="home-hero"
    >
      <div className="mx-auto grid w-full max-w-6xl gap-y-8 px-4 pb-10 pt-6 sm:px-6 sm:pt-10 lg:grid-cols-[minmax(0,23rem)_minmax(0,1fr)] lg:items-center lg:gap-x-10 lg:py-14 xl:grid-cols-[minmax(0,28rem)_minmax(0,1fr)] xl:gap-x-16">
        <div className="lg:[grid-column:1] lg:[grid-row:1]">
          <p className="text-xs font-semibold tracking-[0.08em] text-primary">FREE SAT COURSE · NO ACCOUNT NEEDED</p>
          <h1 className="mt-3 text-[2.6rem] font-bold leading-[1.04] tracking-[-0.035em] sm:mt-4 sm:text-6xl lg:text-[3.3rem] xl:text-[4rem]">
            {/* Three fixed lines: the wrap can't change when the web font lands (CLS). Plain text
                nodes + <br/> keep the H1 itself the LCP element. */}
            The SAT course <br />
            that studies <br />
            you first.
          </h1>
          <p className="mt-3 max-w-[34rem] text-pretty text-sm leading-relaxed text-muted-foreground sm:mt-5 sm:text-lg">
            A {DIAGNOSTIC_FACTS.questions}-question diagnostic finds your weak spots. Every practice set aims there until
            each area turns green.
          </p>

          <div className="mt-6 flex flex-col items-stretch gap-3 sm:items-start lg:mt-8">
            <Button asChild size="lg" className="h-12 w-full text-base sm:w-72">
              {returning ? (
                <Link to={COURSE_HREF}>Continue your course →</Link>
              ) : (
                <Link to={MARKETING_CTAS.home.href}>{MARKETING_CTAS.home.label}</Link>
              )}
            </Button>
            <p className="text-sm text-muted-foreground">
              {DIAGNOSTIC_FACTS.questions} questions · {DIAGNOSTIC_FACTS.minutesLabel} · pause anytime
            </p>
            <a
              href={MARKETING_CTAS.homeSkip.href}
              className="w-fit rounded text-sm font-medium text-foreground/80 underline decoration-foreground/30 underline-offset-4 hover:text-primary hover:decoration-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {MARKETING_CTAS.homeSkip.label}
            </a>
          </div>
        </div>

        {/* The stage: full bleed on phones, a 46rem column from sm, beside the copy from lg, toward the viewport edge at xl. */}
        <div className="-mx-4 sm:mx-0 sm:max-w-[46rem] lg:max-w-none lg:[grid-column:2] lg:[grid-row:1] xl:-mr-[max(0px,calc((100vw-72rem)/2-1.5rem))]">
          <MantaMethod id="how-it-works" autoplay={initial === 'start'} initial={initial} textClassName="px-4 sm:px-0" />
        </div>
      </div>
    </section>
  );
}
